import {
    buildReducer,
    FavoriteEntity,
    getMatchingFavoritePerson,
    PersonType,
    getLogger,
} from '../../../../shared';
import type { FavoritePersonsState } from '../../../state/data/hubDataState';
import { lpcPersonaUpdatedAction } from '../../../actions/lpcActions';
import {
    loadFavoritesCompletedAction,
    addFavoritePersonaClickedAction,
    removeFavoritePersonaClickedAction,
    addFavoritePersonaCompletedAction,
    removeFavoritePersonaCompletedAction,
    contactEditorFavoriteSavedAction,
    editFavoritePersonaStartedAction,
    handleSoftDeletedGroupCompletedAction,
} from '../../../../hub';

export default buildReducer<FavoritePersonsState>()
    .handleDataAction(loadFavoritesCompletedAction, (_state, action) => ({
        favoritePersons: action.payload.favorites,
        areInitialFavoritesLoaded: true,
        favoriteIdsBeingRemoved: [],
        isFavoritingInProgress: false,
    }))
    .handle(addFavoritePersonaClickedAction, (state, action) => ({
        ...state,
        favoritePersons: addFavorite(state.favoritePersons, action.payload.favorite),
        isFavoritingInProgress: true,
    }))
    .handle(removeFavoritePersonaClickedAction, (state, action) => ({
        ...state,
        favoriteIdsBeingRemoved: [...state.favoriteIdsBeingRemoved, action.payload.favoriteId],
        isFavoritingInProgress: true,
    }))
    .handle(contactEditorFavoriteSavedAction, (state, action) => {
        const favoriteWithoutPersonaId = state.favoritePersons.find(
            favorite =>
                favorite.type === PersonType.FavoritePerson &&
                !favorite.personaId &&
                favorite.hubPersonaId === action.payload.matchingFavorite.hubPersonaId
        );

        if (!favoriteWithoutPersonaId) {
            return state;
        }

        return {
            ...state,
            favoritePersons: state.favoritePersons.map(person =>
                person.hubPersonaId === favoriteWithoutPersonaId.hubPersonaId
                    ? {
                          ...person,
                          personaId: action.payload.personaId,
                      }
                    : person
            ),
        };
    })
    .handle(lpcPersonaUpdatedAction, (state, action) => {
        if (action.payload.persona.type !== PersonType.Person) {
            return state;
        }

        const person = action.payload.persona;

        const matchingFavorite = getMatchingFavoritePerson(
            person.personaId,
            person.info.mainEmailAddress,
            state.favoritePersons
        );

        if (!matchingFavorite) {
            return state;
        }

        // If we've updated a persona that is favorited, we alter our copy of userConfiguration to match the current name and email address
        return {
            ...state,
            favoritePersons: state.favoritePersons.map(p =>
                p.hubPersonaId !== matchingFavorite.hubPersonaId
                    ? p
                    : {
                          ...p,
                          displayName: person.info.originalDisplayName,
                          mainEmailAddress: person.info.mainEmailAddress,
                      }
            ),
        };
    })
    .handle(editFavoritePersonaStartedAction, (state, action) => {
        // As Above, this time the favorite API will be notified too, adn this makes me think that we should not have the above method maybe?

        const { favoriteId, updatedFavorite } = action.payload;

        const matchingFavorite = state.favoritePersons.find(
            favorite => favorite.favoriteId === favoriteId
        );

        const newFavorite = { ...matchingFavorite, ...updatedFavorite } as FavoriteEntity;

        if (!matchingFavorite) {
            return state;
        }

        return {
            ...state,
            favoritePersons: state.favoritePersons.map(existingFavorite =>
                existingFavorite.favoriteId !== matchingFavorite.favoriteId
                    ? existingFavorite
                    : newFavorite
            ),
        };
    })
    .handleDataAction(
        addFavoritePersonaCompletedAction,
        (state, action) => {
            if (
                state.favoritePersons.some(
                    favorite => favorite.favoriteId === action.payload.newFavoriteId
                )
            ) {
                // User issued a Create request for an already existing favorite (Upsert).
                // Remove the temporary favorite from the list
                return {
                    ...state,
                    favoritePersons: removeFavorite(
                        state.favoritePersons,
                        null,
                        action.payload.hubPersonaId
                    ),
                    isFavoritingInProgress: false,
                };
            }

            const addedFavoriteIndex = state.favoritePersons.findIndex(
                favorite => favorite.hubPersonaId === action.payload.hubPersonaId
            );

            // Update the favoriteId of newly created favorite
            const newFavorite = {
                ...state.favoritePersons[addedFavoriteIndex],
                favoriteId: action.payload.newFavoriteId,
            };

            const newFavoritePersons = [...state.favoritePersons];
            newFavoritePersons[addedFavoriteIndex] = newFavorite;

            return {
                ...state,
                favoritePersons: newFavoritePersons,
                isFavoritingInProgress: false,
            };
        },
        (state, action) => ({
            ...state,
            favoritePersons: removeFavorite(
                state.favoritePersons,
                null, // At this point, we do not have a favoriteId
                action.payload.hubPersonaId
            ),
            isFavoritingInProgress: false,
        })
    )
    .handleDataAction(
        removeFavoritePersonaCompletedAction,
        (state, action) => {
            return getStateAfterRemoveFavorite(state, action.payload.favoriteId);
        },
        (state, action) => {
            const isItemNotFoundError =
                action.payload.error && action.payload.error.statusCode === 404;

            const updatedFavoritePersons = isItemNotFoundError
                ? removeFavorite(state.favoritePersons, action.payload.favoriteId)
                : [...state.favoritePersons];

            return {
                ...state,
                favoritePersons: updatedFavoritePersons,
                favoriteIdsBeingRemoved: state.favoriteIdsBeingRemoved.filter(
                    favoriteId => favoriteId !== action.payload.favoriteId
                ),
                isFavoritingInProgress: false,
            };
        }
    )
    .handle(handleSoftDeletedGroupCompletedAction, (state, action) => {
        if (!!action.payload.favoriteId) {
            return getStateAfterRemoveFavorite(state, action.payload.favoriteId);
        }
        return state;
    })
    .done({
        favoritePersons: [],
        areInitialFavoritesLoaded: false,
        favoriteIdsBeingRemoved: [],
        isFavoritingInProgress: false,
    });

// When adding a favorite, the hubPersonaId will come from the persona that has been favorited
function addFavorite(
    currentFavorites: FavoriteEntity[],
    favorite: FavoriteEntity
): FavoriteEntity[] {
    return currentFavorites.concat([favorite]);
}

// Tries to remove the given favoriteId from the list of current favorites.
// If a favorite was in progress of being added, its favoriteId will be undefined.
// Use the hubPersonaId in that case only
function removeFavorite(
    currentFavorites: FavoriteEntity[],
    favoriteId: string,
    hubPersonaId?: string
): FavoriteEntity[] {
    if (!(favoriteId || hubPersonaId)) {
        getLogger().log('removeFavorite called with no favoriteId or hubPersonaId. This is a bug.');
        return currentFavorites;
    }

    return currentFavorites.filter(currentFavorite =>
        favoriteId
            ? currentFavorite.favoriteId !== favoriteId
            : currentFavorite.hubPersonaId !== hubPersonaId
    );
}

function getStateAfterRemoveFavorite(
    state: FavoritePersonsState,
    favoriteId: string
): FavoritePersonsState {
    return {
        ...state,
        favoritePersons: removeFavorite(state.favoritePersons, favoriteId),
        favoriteIdsBeingRemoved: state.favoriteIdsBeingRemoved.filter(
            deletedFavoriteId => deletedFavoriteId !== favoriteId
        ),
        isFavoritingInProgress: false,
    };
}
export type { Action } from '../../../../shared';
